import { isTimeZone } from '@/ui/zonedTime';

const NOON = 12;
const SIX_IN_THE_EVENING = 18;

/**
 * The greeting for the hour it is in the workspace.
 *
 * The hour is the workspace's, not the browser's, so a coordinator travelling reads the same
 * "Good morning" as the office does.
 *
 * @param now the current instant, in epoch milliseconds
 * @param zone the workspace's IANA time zone; a zone the platform lacks falls back to the browser's
 * @returns "Good morning", "Good afternoon" or "Good evening"
 */
export function greetingFor(now: number, zone: string): string {
  // h23, not hour12: false, which reads midnight as 24 in some engines and would call it evening.
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: isTimeZone(zone) ? zone : undefined,
    hour: 'numeric',
    hourCycle: 'h23',
  });
  const hour = Number(formatter.format(now));
  if (hour < NOON) {
    return 'Good morning';
  }
  return hour < SIX_IN_THE_EVENING ? 'Good afternoon' : 'Good evening';
}

/**
 * The part of a name to greet someone by.
 *
 * @param displayName the name as the workspace holds it
 * @returns the first word, or an empty string for a blank name
 */
export function firstNameOf(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? '';
}

/**
 * How much is waiting, said as a sentence.
 *
 * @param total everything waiting for the person
 * @returns for example "12 things are waiting for you."
 */
export function waitingPhrase(total: number): string {
  if (total === 1) {
    return '1 thing is waiting for you.';
  }
  return `${String(total)} things are waiting for you.`;
}

/**
 * The line under the page title.
 *
 * @param greeting the greeting for the hour
 * @param name the first name to greet, if there is one
 * @param total everything waiting for the person
 * @returns for example "Good morning, Dana. 12 things are waiting for you."
 */
export function ledeFor(greeting: string, name: string, total: number): string {
  const hello = name === '' ? `${greeting}.` : `${greeting}, ${name}.`;
  return total > 0 ? `${hello} ${waitingPhrase(total)}` : hello;
}
